import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { functionHeaders, isTokenExpired, setAccessToken } from '@dia/db';
import { supabase } from './supabase';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from './env';
import { mergeStayRefresh } from './screens/logic/staySession';

/**
 * The guest session.
 *
 * A guest is not a Supabase Auth user. They present a stay code and a last
 * name to the guest-login edge function, which checks the access window and
 * mints a JWT whose claims the policies read. The token lives in localStorage
 * so the app opens signed in, which matters on an island where signing in
 * again may not be possible for twenty minutes.
 */

export type GuestSession = {
  access_token: string;
  window: 'in' | 'out';
  public_only: boolean;
  stay: { id: string; tier: 'all_inclusive' | 'standard'; arrives_on: string; departs_on: string; status: string };
  guest: { id: string; first_name: string; is_lead: boolean; consent_at: string | null };
};

export type AuthState = {
  session: GuestSession | null;
  loading: boolean;
  error: { es: string; en: string } | null;
  signIn: (code: string, lastName: string, consent: boolean) => Promise<boolean>;
  signOut: () => void;
};

const STORAGE_KEY = 'dia.guest.session';
const AuthContext = createContext<AuthState | null>(null);

/**
 * The stored session, unless its token has expired.
 *
 * A dead token is worse than no token: every read comes back refused and the
 * app looks broken rather than signed out. Signing the guest back in is one
 * stay code and a last name.
 */
function readStored(): GuestSession | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as GuestSession;
    if (isTokenExpired(stored.access_token)) {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<GuestSession | null>(readStored);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ es: string; en: string } | null>(null);

  useEffect(() => {
    setAccessToken(supabase, session?.access_token ?? null);
  }, [session]);

  /**
   * The stay's own facts (dates, tier, status) were a snapshot from the
   * moment of sign-in and never checked again, so a stay extended or
   * corrected after a guest signed in stayed wrong on their phone until they
   * signed out and back in. This quietly asks for the current row whenever
   * there is a reason to (app open, regaining a connection, coming back to
   * the tab), the same triggers the outbox sync engine already uses. A
   * failure changes nothing: the guest keeps seeing the last known good
   * values, never an error, matching how every other stale value in this
   * app behaves.
   */
  useEffect(() => {
    const stayId = session?.stay.id;
    if (!stayId) return;

    let cancelled = false;
    const refresh = async () => {
      if (!navigator.onLine) return;
      const { data, error } = await supabase
        .from('stays')
        .select('tier, arrives_on, departs_on, status')
        .eq('id', stayId)
        .maybeSingle();
      if (cancelled || error || !data) return;

      setSession((prev) => {
        if (!prev) return prev;
        const next = mergeStayRefresh(prev, stayId, data);
        if (next === prev) return prev;
        try {
          globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          // Private mode. The refreshed value still holds for this tab.
        }
        return next;
      });
    };

    void refresh();
    globalThis.addEventListener?.('online', refresh);
    globalThis.addEventListener?.('focus', refresh);
    document.addEventListener?.('visibilitychange', refresh);
    return () => {
      cancelled = true;
      globalThis.removeEventListener?.('online', refresh);
      globalThis.removeEventListener?.('focus', refresh);
      document.removeEventListener?.('visibilitychange', refresh);
    };
    // Only the id decides whether there is anything to refresh; re-running
    // this for every session change would refresh right after we just wrote
    // the refreshed value, forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.stay.id]);

  const signIn = useCallback(async (code: string, lastName: string, consent: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/guest-login`, {
        method: 'POST',
        // Authorization as well as apikey (defect D1). A deployed function sits
        // behind a gate that answers 401 before the function runs when the
        // header is missing, and signing in is exactly when nobody has a token
        // yet, so the anon key stands in for one.
        headers: functionHeaders({ anonKey: SUPABASE_ANON_KEY }),
        body: JSON.stringify({ code, last_name: lastName, consent }),
      });
      const body = await response.json();

      if (!response.ok) {
        setError({ es: String(body.message_es ?? ''), en: String(body.message_en ?? '') });
        return false;
      }

      setSession(body as GuestSession);
      try {
        globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(body));
      } catch {
        // Private mode. The session still works until the tab closes.
      }
      return true;
    } catch {
      setError({
        es: 'No hay señal ahora mismo. Intenta otra vez en un momento.',
        en: 'There is no signal right now. Try again in a moment.',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(() => {
    setSession(null);
    try {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clean up.
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ session, loading, error, signIn, signOut }),
    [session, loading, error, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth needs an AuthProvider above it.');
  return ctx;
}
